// WHAT: Securities overview component with filtering, sorting, and search capabilities
// WHY:  Provides comprehensive interface for viewing and managing all securities in the portfolio
// DOMAIN: Investments → Parqet → Securities Management
// DEPENDENCIES: getSecuritiesFromSupabase service, Security types, React state management
// RISKS:
//   - NOTE: Loads all securities at once - may be slow with large portfolios
//   - NOTE: Search and filtering are client-side - may be inefficient with many securities
//   - WARN: No pagination - all securities loaded into memory

import React, { useState, useEffect, useMemo } from "react";

import { Card } from "@/components/ui";

import { getSecuritiesFromSupabase, type SecurityEntry } from "../services/api";

interface SecuritiesOverviewProperties {
  className?: string;
}

/**
 * WHAT:
 *   Securities overview component displaying all securities with filtering, sorting, and search.
 *
 * WHY:
 *   Provides comprehensive interface for viewing and managing all securities in the portfolio.
 *
 * PROPS:
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Loads all securities from Supabase on mount
 *   - Provides search functionality by name or ISIN
 *   - Filters by sector (all sectors or specific sector)
 *   - Sorts by name, ISIN, or sector
 *   - Displays securities in card grid layout
 *
 * RISKS:
 *   - NOTE: Loads all securities at once - may be slow with large portfolios
 *   - NOTE: Search and filtering are client-side - may be inefficient with many securities
 *   - WARN: No pagination - all securities loaded into memory
 *   - NOTE: Error handling only shows error message - no retry mechanism
 *
 * @param root0
 *
 * @param root0.className
 *
 * @returns {JSX.Element} The SecuritiesOverview component
 *
 */
export const SecuritiesOverview: React.FC<SecuritiesOverviewProperties> = ({ className }) => {
  const [securities, setSecurities] = useState<Record<string, SecurityEntry>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSector, setFilterSector] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"isin" | "name" | "sector">("name");

  // Load all securities (in production, use pagination)
  useEffect(() => {
    void loadSecurities();
  }, []);

  const loadSecurities = async () => {
    setLoading(true);
    setError(null);
    try {
      // NOTE: Get ISINs from database or cache
      // For now, empty array (will be populated after enrichment)
      const result = await getSecuritiesFromSupabase([]);

      if (result.results?.securities) {
        const filteredSecurities: Record<string, SecurityEntry> = {};
        for (const [isin, entry] of Object.entries(result.results.securities)) {
          if (entry) {
            filteredSecurities[isin] = entry;
          }
        }
        setSecurities(filteredSecurities);
      }
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to load securities";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get unique sectors for filter
  const sectors = useMemo(() => {
    const sectorSet = new Set<string>();
    const entries: SecurityEntry[] = Object.values(securities);
    for (const entry of entries) {
      const sector = entry.securities.sector;
      if (typeof sector === "string" && sector.length > 0) {
        sectorSet.add(sector);
      }
    }
    const sectorArray: string[] = [...sectorSet];
    // Reason: toSorted returns array but TypeScript needs explicit type

    const sortedArray: unknown = sectorArray.toSorted((a: string, b: string) => a.localeCompare(b));
    const sorted: string[] = sortedArray as string[];
    return sorted;
  }, [securities]);

  // Filter and sort securities
  const filteredSecurities = useMemo(() => {
    const entries: [string, SecurityEntry][] = Object.entries(securities);
    let filtered: [string, SecurityEntry][] = entries;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(([isin, entry]: [string, SecurityEntry]) => {
        return (
          entry.securities.name.toLowerCase().includes(term) ||
          isin.toLowerCase().includes(term) ||
          entry.securities.ticker.toLowerCase().includes(term)
        );
      });
    }

    // Sector filter
    if (filterSector !== "all") {
      filtered = filtered.filter(
        ([, entry]: [string, SecurityEntry]) => entry.securities.sector === filterSector,
      );
    }

    // Sort
    // Reason: toSorted returns array but TypeScript needs explicit type

    const sortedArray: unknown = filtered.toSorted(
      ([, a]: [string, SecurityEntry], [, b]: [string, SecurityEntry]) => {
        switch (sortBy) {
          case "name": {
            return a.securities.name.localeCompare(b.securities.name);
          }
          case "isin": {
            return a.securities.figi.localeCompare(b.securities.figi);
          }
          case "sector": {
            return (a.securities.sector ?? "").localeCompare(b.securities.sector ?? "");
          }
          default: {
            return 0;
          }
        }
      },
    );
    const sorted: [string, SecurityEntry][] = sortedArray as [string, SecurityEntry][];
    return sorted;
  }, [securities, searchTerm, filterSector, sortBy]);

  if (loading) {
    return (
      <Card className={className ?? ""}>
        <div className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-gray-400">Loading securities...</div>
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className ?? ""}>
        <div className="p-6">
          <div className="text-red-500">Error: {error}</div>
          <button
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => {
              void loadSecurities();
            }}
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className={className ?? ""}>
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Securities Overview</h2>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              onClick={() => {
                void loadSecurities();
              }}
            >
              Refresh
            </button>
          </div>

          {/* Filters and Search */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
              <input
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                placeholder="Search by name, ISIN, or ticker..."
                type="text"
                value={searchTerm}
              />
            </div>

            {/* Sector Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Sector</label>
              <select
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                onChange={(e) => {
                  setFilterSector(e.target.value);
                }}
                value={filterSector}
              >
                <option value="all">All Sectors</option>
                {sectors.map((sector) => (
                  <option key={sector} value={sector}>
                    {sector}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Sort By</label>
              <select
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                onChange={(e) => {
                  setSortBy(e.target.value as "isin" | "name" | "sector");
                }}
                value={sortBy}
              >
                <option value="name">Name</option>
                <option value="isin">ISIN</option>
                <option value="sector">Sector</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-6 text-gray-400 text-sm">
            Showing {String(filteredSecurities.length)} of {String(Object.keys(securities).length)}{" "}
            securities
          </div>

          {/* Securities Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-3 text-gray-300 font-medium">Name</th>
                  <th className="text-left p-3 text-gray-300 font-medium">ISIN</th>
                  <th className="text-left p-3 text-gray-300 font-medium">Ticker</th>
                  <th className="text-left p-3 text-gray-300 font-medium">Sector</th>
                  <th className="text-left p-3 text-gray-300 font-medium">Industry</th>
                  <th className="text-left p-3 text-gray-300 font-medium">Country</th>
                </tr>
              </thead>
              <tbody>
                {filteredSecurities.length === 0 ? (
                  <tr>
                    <td className="text-center p-6 text-gray-400" colSpan={6}>
                      No securities found. Import transactions to automatically enrich securities.
                    </td>
                  </tr>
                ) : (
                  filteredSecurities.map(([isin, entry]: [string, SecurityEntry]) => {
                    const entryTyped: SecurityEntry = entry;
                    const securities = entryTyped.securities;
                    return (
                      <tr
                        className="border-b border-gray-800 hover:bg-gray-800/50 transition cursor-pointer"
                        key={isin}
                        onClick={() => {
                          // NOTE: Navigate to detail view - navigation handled elsewhere
                        }}
                      >
                        <td className="p-3 text-white font-medium">{securities.name}</td>
                        <td className="p-3 text-gray-400 font-mono text-sm">{isin}</td>
                        <td className="p-3 text-blue-400">{securities.ticker}</td>
                        <td className="p-3 text-gray-300">{securities.sector ?? "-"}</td>
                        <td className="p-3 text-gray-300 text-sm">{securities.industry ?? "-"}</td>
                        <td className="p-3 text-gray-300">{securities.country ?? "-"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};
